/**
 * PRD.234 — Provider Availability & Appointment Slot Booking.
 *
 * Data access for `appointment_availability_rules` and `appointment_slots`,
 * plus client-side helpers for slot generation, conflict validation, and
 * booking summaries. All queries run against the PowerSync SQLite client.
 *
 * Final authoritative validation must also run server-side in the syncWrite
 * write path — see `packages/syncwrite-core/src/table-registry.ts`.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Constants ───

export const DAYS_OF_WEEK = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export const SLOT_TYPES = [
  'REGULAR',
  'SAME_DAY',
  'WALK_IN',
  'FOLLOW_UP',
  'PROCEDURE',
  'EMERGENCY',
] as const;

export type SlotType = (typeof SLOT_TYPES)[number];

export const SLOT_STATUSES = ['AVAILABLE', 'BLOCKED', 'CANCELLED'] as const;
export type SlotStatus = (typeof SLOT_STATUSES)[number];

export const SLOT_SOURCES = ['AUTO', 'MANUAL'] as const;
export type SlotSource = (typeof SLOT_SOURCES)[number];

/**
 * Appointment status labels considered "active" — i.e. they consume slot
 * capacity and block overlapping bookings for the same provider/patient.
 *
 * The codebase stores status as a label on `appointment_statuses` (no
 * canonical enum). Match the default fixture labels (PRD.234 §22). If a
 * clinic renames a status, that custom label will not be counted unless
 * explicitly added here or to the server-side mirror.
 */
export const ACTIVE_APPOINTMENT_STATUS_LABELS = [
  'Scheduled',
  'Confirmed',
  'Checked In',
  'In Progress',
] as const;

// ─── Row Types ───

export interface AppointmentAvailabilityRuleRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  name: string | null;
  provider_id: string | null;
  service_id: string | null;
  /** JSON-stringified array of `DayOfWeek` values, e.g. '["MONDAY","FRIDAY"]'. */
  days_of_week: string | null;
  start_time: string | null; // HH:mm[:ss]
  end_time: string | null; // HH:mm[:ss]
  slot_duration_minutes: number | null;
  capacity_per_slot: number | null;
  slot_type: string | null;
  effective_start_date: string | null; // YYYY-MM-DD
  effective_end_date: string | null; // YYYY-MM-DD
  auto_generate: number | null; // 0/1
  generate_days_ahead: number | null;
  notes: string | null;
  is_active: number | null; // 0/1
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface AppointmentSlotRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  availability_rule_id: string | null;
  provider_id: string | null;
  service_id: string | null;
  slot_date: string | null; // YYYY-MM-DD
  start_time: string | null; // ISO string
  end_time: string | null; // ISO string
  capacity: number | null;
  slot_type: string | null;
  status: string | null;
  source: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

/** Slot row with a computed `booked_count`. Returned by listing queries. */
export interface AppointmentSlotWithBookingRow extends AppointmentSlotRow {
  booked_count: number | null;
}

// ─── UI Mapping ───

export interface AvailabilityRuleUI {
  id: string;
  name: string;
  providerId: string;
  serviceId: string;
  daysOfWeek: DayOfWeek[];
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  slotDurationMinutes: number;
  capacityPerSlot: number;
  slotType: SlotType;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  autoGenerate: boolean;
  generateDaysAhead: number;
  notes: string;
  isActive: boolean;
}

export function parseDaysOfWeek(raw: string | null): DayOfWeek[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((d): d is DayOfWeek => DAYS_OF_WEEK.includes(d as DayOfWeek));
  } catch {
    return [];
  }
}

/** Strip seconds from a "HH:mm:ss" Postgres time so it fits `<input type="time">`. */
function normalizeTime(t: string | null | undefined): string {
  if (!t) return '';
  return t.length >= 5 ? t.slice(0, 5) : t;
}

export function mapAvailabilityRuleRowToUI(
  row: AppointmentAvailabilityRuleRow,
): AvailabilityRuleUI {
  return {
    id: row.id,
    name: row.name ?? '',
    providerId: row.provider_id ?? '',
    serviceId: row.service_id ?? '',
    daysOfWeek: parseDaysOfWeek(row.days_of_week),
    startTime: normalizeTime(row.start_time),
    endTime: normalizeTime(row.end_time),
    slotDurationMinutes: row.slot_duration_minutes ?? 30,
    capacityPerSlot: row.capacity_per_slot ?? 1,
    slotType: (row.slot_type as SlotType) ?? 'REGULAR',
    effectiveStartDate: row.effective_start_date,
    effectiveEndDate: row.effective_end_date,
    autoGenerate: row.auto_generate === 1,
    generateDaysAhead: row.generate_days_ahead ?? 60,
    notes: row.notes ?? '',
    isActive: row.is_active === 1,
  };
}

export function mapUIToAvailabilityRuleColumns(ui: Omit<AvailabilityRuleUI, 'id'>) {
  return {
    name: ui.name,
    provider_id: ui.providerId,
    service_id: ui.serviceId,
    days_of_week: JSON.stringify(ui.daysOfWeek),
    start_time: ui.startTime,
    end_time: ui.endTime,
    slot_duration_minutes: ui.slotDurationMinutes,
    capacity_per_slot: ui.capacityPerSlot,
    slot_type: ui.slotType,
    effective_start_date: ui.effectiveStartDate,
    effective_end_date: ui.effectiveEndDate,
    auto_generate: ui.autoGenerate ? 1 : 0,
    generate_days_ahead: ui.generateDaysAhead,
    notes: ui.notes || null,
    is_active: ui.isActive ? 1 : 0,
    updated_at: new Date().toISOString(),
  };
}

// ─── Queries: Availability Rules ───

export function qAvailabilityRules(): QuerySpec<[]> {
  return {
    key: ['appointment-availability-rules'] as const,
    sql: `SELECT * FROM appointment_availability_rules
          WHERE deleted_at IS NULL
          ORDER BY name ASC`,
    params: [],
    watch: ['appointment_availability_rules'],
  };
}

export function qAvailabilityRuleById(id: string): QuerySpec<[string]> {
  return {
    key: ['appointment-availability-rules', 'by-id', id] as const,
    sql: `SELECT * FROM appointment_availability_rules WHERE id = ? LIMIT 1`,
    params: [id],
    watch: ['appointment_availability_rules'],
  };
}

// ─── Queries: Slots ───

/**
 * List slots in a date range with booked counts.
 * Joins active appointments to compute remaining capacity client-side.
 */
export function qAppointmentSlotsInRange(
  startDate: string,
  endDate: string,
): QuerySpec<[string, string]> {
  return {
    key: ['appointment-slots', 'in-range', startDate, endDate] as const,
    sql: `SELECT s.*, (
            SELECT COUNT(*) FROM appointments a
            WHERE a.appointment_slot_id = s.id
          ) AS booked_count
          FROM appointment_slots s
          WHERE s.deleted_at IS NULL
            AND s.slot_date >= ? AND s.slot_date <= ?
          ORDER BY s.slot_date ASC, s.start_time ASC`,
    params: [startDate, endDate],
    watch: ['appointment_slots', 'appointments'],
  };
}

/**
 * List available slots for a specific provider/service/date — used by
 * BookAppointmentDrawer to render selectable cards. Hides BLOCKED/CANCELLED
 * slots by default. The `FULL` filter is applied client-side using the
 * `booked_count` aggregate.
 */
export function qAvailableSlotsForBooking(
  providerId: string,
  serviceId: string,
  date: string,
): QuerySpec<[string, string, string]> {
  return {
    key: ['appointment-slots', 'available', providerId, serviceId, date] as const,
    sql: `SELECT s.*, (
            SELECT COUNT(*) FROM appointments a
            WHERE a.appointment_slot_id = s.id
          ) AS booked_count
          FROM appointment_slots s
          WHERE s.deleted_at IS NULL
            AND s.provider_id = ?
            AND s.service_id = ?
            AND s.slot_date = ?
            AND s.status = 'AVAILABLE'
          ORDER BY s.start_time ASC`,
    params: [providerId, serviceId, date],
    watch: ['appointment_slots', 'appointments'],
  };
}

export function qSlotsByRule(ruleId: string): QuerySpec<[string]> {
  return {
    key: ['appointment-slots', 'by-rule', ruleId] as const,
    sql: `SELECT * FROM appointment_slots
          WHERE deleted_at IS NULL AND availability_rule_id = ?
          ORDER BY slot_date ASC, start_time ASC`,
    params: [ruleId],
    watch: ['appointment_slots'],
  };
}

export function qSlotById(slotId: string): QuerySpec<[string]> {
  return {
    key: ['appointment-slots', 'by-id', slotId] as const,
    sql: `SELECT s.*, (
            SELECT COUNT(*) FROM appointments a
            WHERE a.appointment_slot_id = s.id
          ) AS booked_count
          FROM appointment_slots s
          WHERE s.id = ? LIMIT 1`,
    params: [slotId],
    watch: ['appointment_slots', 'appointments'],
  };
}

// ─── Slot Generation ───

export interface SlotGenerationInput {
  rule: AvailabilityRuleUI;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

export interface GeneratedSlotDraft {
  slotDate: string; // YYYY-MM-DD
  startTime: string; // ISO
  endTime: string; // ISO
}

/**
 * Iterate dates in [startDate, endDate] and emit one draft slot for each
 * (matching day-of-week, slot interval). Pure client-side computation; the
 * caller is responsible for skipping existing slots and writing to the DB.
 */
export function generateSlotDrafts({
  rule,
  startDate,
  endDate,
}: SlotGenerationInput): GeneratedSlotDraft[] {
  const drafts: GeneratedSlotDraft[] = [];

  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end || start > end) return drafts;

  const effectiveStart = rule.effectiveStartDate ? parseDateOnly(rule.effectiveStartDate) : null;
  const effectiveEnd = rule.effectiveEndDate ? parseDateOnly(rule.effectiveEndDate) : null;

  const [startH, startM] = parseHhmm(rule.startTime);
  const [endH, endM] = parseHhmm(rule.endTime);
  const durationMinutes = rule.slotDurationMinutes;
  if (durationMinutes <= 0) return drafts;

  const dayWindowMinutes = endH * 60 + endM - (startH * 60 + startM);
  if (dayWindowMinutes <= 0) return drafts;

  const days = new Set(rule.daysOfWeek);

  for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
    if (effectiveStart && d < effectiveStart) continue;
    if (effectiveEnd && d > effectiveEnd) continue;
    if (!days.has(DAYS_OF_WEEK[d.getDay()])) continue;

    const dayStr = toIsoDate(d);
    let cursorMinutes = startH * 60 + startM;
    const dayEndMinutes = endH * 60 + endM;
    while (cursorMinutes + durationMinutes <= dayEndMinutes) {
      drafts.push({
        slotDate: dayStr,
        // Clinic wall-clock time is stored as UTC. The server reconstructs
        // appointment timestamps the same way (`${date}T${HH:mm:ss}Z`), so
        // slot.start_time and the booked appointment's derived timestamp
        // match exactly regardless of the user's browser timezone.
        startTime: wallClockUtc(dayStr, cursorMinutes),
        endTime: wallClockUtc(dayStr, cursorMinutes + durationMinutes),
      });
      cursorMinutes += durationMinutes;
    }
  }

  return drafts;
}

function wallClockUtc(date: string, totalMinutes: number): string {
  const hh = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const mm = String(totalMinutes % 60).padStart(2, '0');
  return `${date}T${hh}:${mm}:00.000Z`;
}

// ─── Booking Conflict Validation (client-side hint) ───

export interface BookingConflictInput {
  slot?: AppointmentSlotWithBookingRow | null;
  providerId: string;
  patientId: string;
  startIso: string;
  endIso: string;
  /** When editing, exclude this appointment id from overlap checks. */
  excludeAppointmentId?: string;
  /** Active appointments to scan for overlap, with their start/end ISO timestamps. */
  existingAppointments: Array<{
    id: string;
    provider_id: string | null;
    patient_id: string | null;
    start_iso: string;
    end_iso: string;
    is_active: boolean;
  }>;
}

export type BookingConflictReason =
  | 'SLOT_NOT_AVAILABLE'
  | 'SLOT_BLOCKED'
  | 'SLOT_CANCELLED'
  | 'SLOT_FULL'
  | 'SLOT_TIME_MISMATCH'
  | 'PROVIDER_CONFLICT'
  | 'PATIENT_CONFLICT';

export interface BookingConflictResult {
  ok: boolean;
  reason?: BookingConflictReason;
  message?: string;
}

const CONFLICT_MESSAGES: Record<BookingConflictReason, string> = {
  SLOT_NOT_AVAILABLE: 'This slot is no longer available. Please choose another time.',
  SLOT_BLOCKED: 'This slot is blocked and cannot be booked.',
  SLOT_CANCELLED: 'This slot has been cancelled and cannot be booked.',
  SLOT_FULL: 'This slot is no longer available. Please choose another time.',
  SLOT_TIME_MISMATCH: 'Appointment time must match the selected slot.',
  PROVIDER_CONFLICT: 'This provider already has an appointment during this time.',
  PATIENT_CONFLICT: 'This patient already has an appointment during this time.',
};

export function validateAppointmentBookingConflict(
  input: BookingConflictInput,
): BookingConflictResult {
  const { slot, providerId, patientId, startIso, endIso, existingAppointments } = input;

  if (slot) {
    if (slot.status === 'BLOCKED') return fail('SLOT_BLOCKED');
    if (slot.status === 'CANCELLED') return fail('SLOT_CANCELLED');
    if (slot.status !== 'AVAILABLE') return fail('SLOT_NOT_AVAILABLE');
    const booked = slot.booked_count ?? 0;
    const cap = slot.capacity ?? 1;
    if (booked >= cap) return fail('SLOT_FULL');
    if (slot.start_time !== startIso || slot.end_time !== endIso) {
      return fail('SLOT_TIME_MISMATCH');
    }
  }

  const newStart = Date.parse(startIso);
  const newEnd = Date.parse(endIso);

  for (const a of existingAppointments) {
    if (!a.is_active) continue;
    if (input.excludeAppointmentId && a.id === input.excludeAppointmentId) continue;
    const eStart = Date.parse(a.start_iso);
    const eEnd = Date.parse(a.end_iso);
    if (!overlaps(eStart, eEnd, newStart, newEnd)) continue;

    if (a.provider_id && a.provider_id === providerId) return fail('PROVIDER_CONFLICT');
    if (a.patient_id && a.patient_id === patientId) return fail('PATIENT_CONFLICT');
  }

  return { ok: true };
}

function fail(reason: BookingConflictReason): BookingConflictResult {
  return { ok: false, reason, message: CONFLICT_MESSAGES[reason] };
}

function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && aEnd > bStart;
}

// ─── Date helpers ───

function parseDateOnly(s: string): Date | null {
  // Accept 'YYYY-MM-DD' — interpret as local midnight to match clinic time.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function parseHhmm(s: string): [number, number] {
  const m = /^(\d{1,2}):(\d{2})/.exec(s ?? '');
  if (!m) return [0, 0];
  return [Number(m[1]), Number(m[2])];
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
